import InfoCircleOutlined from "@ant-design/icons/lib/icons/InfoCircleOutlined";
import { printUserFriendlyDuration } from "@p0security/shared/permission-requests/util";
import {
  ApprovalRule,
  UserApprovalOptions,
  isDurationLimited,
} from "@p0security/shared/types/policy/types";
import { Flex, Tooltip } from "@p0security/ui";
import { useMemo } from "react";
import { StyledTag } from "styles/StyledTags";
import { colors } from "styles/variables";

type OptionEntry = { key: string; label: string; important: boolean };

const OPTIONS: OptionEntry[] = [
  { key: "allowOneParty", label: "Allow one party", important: true },
  { key: "breakGlassApprover", label: "Break-glass access", important: true },
  { key: "requireReason", label: "Require reason", important: false },
  { key: "requireDuration", label: "Require duration", important: false },
  { key: "requirePreapproval", label: "Require preapproval", important: false },
];

type Props = {
  approvalOptions: UserApprovalOptions;
};

export const ApprovalOptionsPreview: React.FC<Props> = ({
  approvalOptions,
}) => {
  const { inline, truncated } = useMemo(() => {
    const active = OPTIONS.filter(
      (o) => approvalOptions[o.key as keyof UserApprovalOptions]
    );
    const important = active.filter((o) => o.important);
    const rest = active.filter((o) => !o.important);
    const shown = important.length > 0 ? important : rest.slice(0, 1);
    const hidden = important.length > 0 ? rest : rest.slice(1);
    return {
      inline: hidden.length === 1 ? [...shown, ...hidden] : shown,
      truncated: hidden.length === 1 ? [] : hidden,
    };
  }, [approvalOptions]);

  // Render nothing, not an empty box, so callers' gaps don't double up
  if (inline.length === 0) return null;

  return (
    // Tags drop antd's built-in right margin so `gap` alone sets the spacing
    <Flex wrap gap={8} align="center">
      {inline.map((o) => (
        <StyledTag
          key={o.key}
          color={o.important ? colors.tagColors.red : colors.tagColors.grey}
          className="me-0!"
        >
          {o.label}
        </StyledTag>
      ))}
      {truncated.length > 0 && (
        <Tooltip title={truncated.map((o) => o.label).join(", ")}>
          <StyledTag
            color={colors.tagColors.grey}
            className="me-0! cursor-pointer rounded-full"
          >
            +{truncated.length} Actions
          </StyledTag>
        </Tooltip>
      )}
    </Flex>
  );
};

export const MaxDurationTag: React.FC<{
  approval: ApprovalRule;
  className?: string;
}> = ({ approval, className }) => {
  if (!isDurationLimited(approval) || !approval.maxDuration) {
    return null;
  }
  const duration = printUserFriendlyDuration(approval.maxDuration);
  return (
    <Flex className={className} gap={8} align="center">
      <StyledTag color={colors.tagColors.grey} className="me-0!">
        Maximum duration: {duration}
      </StyledTag>
      <Tooltip title={`Matches requests for ${duration} or less.`}>
        <InfoCircleOutlined />
      </Tooltip>
    </Flex>
  );
};
